import type {
  GuidedAnswer,
  GuidedModeDefinition,
  GuidedModeObservation,
} from '../types/guided-mode.types'

export function guidedObservationName(definition: GuidedModeDefinition, conceptId: string): string {
  return definition.concepts[conceptId]?.code?.code || conceptId.replace(/^concept\./, '')
}

export function buildGuidedModeObservations(
  definition: GuidedModeDefinition,
  answers: Record<string, GuidedAnswer>,
  includedQuestionConceptIds?: ReadonlySet<string>,
): GuidedModeObservation[] {
  const questions = Object.values(definition.questions)
  const aggregateConceptIds = new Set(questions
    .filter(question => question.outputs?.length)
    .map(question => question.conceptId))
  const includedOutputConceptIds = new Set(questions
    .filter(question => !includedQuestionConceptIds || includedQuestionConceptIds.has(question.conceptId))
    .flatMap(question => question.outputs?.map(output => output.conceptId) || []))

  const fixed = definition.fixedObservations || []
  const observations = Object.entries(answers).flatMap(([conceptId, answer]) => {
    if (answer === undefined || answer === null || Array.isArray(answer)) return []
    if (aggregateConceptIds.has(conceptId)) return []
    if (includedQuestionConceptIds
      && !includedQuestionConceptIds.has(conceptId)
      && !includedOutputConceptIds.has(conceptId)) return []

    const concept = definition.concepts[conceptId]
    if (!concept) return []

    const conceptType = concept.value?.type
    const value = answer === 'unknown' && (conceptType === 'number' || conceptType === 'duration' || conceptType === 'blood_pressure')
      ? 'Unknown'
      : answer

    return [{
      name: guidedObservationName(definition, conceptId),
      value,
      type: observationType(conceptType),
      category: concept.category || 'clinical_observation',
      source: 'response' as const,
    }]
  })
  return [
    ...observations.filter(observation => !fixed.some(value => value.name === observation.name)),
    ...fixed.map(observation => ({ ...observation })),
  ]
}

function observationType(conceptType?: string): GuidedModeObservation['type'] {
  if (conceptType === 'number' || conceptType === 'duration') return 'number'
  if (conceptType === 'date') return 'date'
  if (conceptType === 'text' || conceptType === 'blood_pressure') return 'text'
  return 'option'
}
